<template>
  <div class="space-y-6">
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="text-gray-600">Loading schema...</div>
    </div>

    <template v-else>
      <div v-if="stats?.schema_loaded" class="bg-green-50 border border-green-200 rounded-lg p-4">
        <div class="flex items-center space-x-2">
          <svg class="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="font-medium text-green-900">
            Schema Loaded: {{ schemaStats.total_nodes }} nodes, {{ schemaStats.total_edges }} relationships
          </span>
        </div>
      </div>

      <div v-else class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div class="flex items-center space-x-2">
          <svg class="w-5 h-5 text-yellow-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 8V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="font-medium text-yellow-900">
            No schema loaded. Place ontology_schema_index_wool_v2.json in the project root.
          </span>
        </div>
      </div>

      <div v-if="exportReadiness.length > 0" class="bg-white border border-gray-200 rounded-lg p-6">
        <h3 class="font-semibold text-gray-900 mb-4 flex items-center">
          <svg class="w-5 h-5 mr-2 text-green-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3.27002 6.96L12 12.01L20.73 6.96" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 22.08V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Export Readiness Status
        </h3>
        <div class="space-y-3">
          <div
            v-for="(item, index) in exportReadiness"
            :key="index"
            :class="[
              'p-4 rounded border',
              item.export_ready ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            ]"
          >
            <div class="flex items-center justify-between mb-2">
              <span class="font-medium text-gray-900">
                {{ item.consignment_id }}
              </span>
              <span
                :class="[
                  'px-2 py-1 text-xs rounded',
                  item.export_ready ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                ]"
              >
                {{ item.export_ready ? 'Ready' : 'Not Ready' }}
              </span>
            </div>
            <div v-if="!item.export_ready && item.reasons" class="text-sm text-gray-700 space-y-1">
              <div v-for="(reason, idx) in item.reasons" :key="idx">• {{ reason }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="nodeTypes.length > 0" class="bg-white border border-gray-200 rounded-lg p-6">
        <h3 class="font-semibold text-gray-900 mb-4 flex items-center">
          <svg class="w-5 h-5 mr-2 text-green-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Schema Node Types
        </h3>
        <div class="grid grid-cols-3 gap-3">
          <button
            v-for="nodeType in nodeTypes"
            :key="nodeType"
            @click="fetchNodesOfType(nodeType)"
            :class="[
              'p-3 rounded border transition-colors',
              selectedNodeType === nodeType
                ? 'bg-green-50 border-green-300'
                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
            ]"
          >
            <div class="font-medium text-gray-900">{{ nodeType }}</div>
            <div class="text-sm text-gray-600">
              {{ schemaStats.nodes_by_type[nodeType] }} nodes
            </div>
          </button>
        </div>
      </div>

      <div v-if="selectedNodeType && nodes.length > 0" class="bg-white border border-gray-200 rounded-lg p-6">
        <h3 class="font-semibold text-gray-900 mb-4">
          {{ selectedNodeType }} Nodes ({{ nodes.length }})
        </h3>
        <div class="max-h-96 overflow-y-auto space-y-2">
          <div
            v-for="(node, index) in nodes"
            :key="index"
            class="p-3 bg-gray-50 rounded border border-gray-200"
          >
            <pre class="text-xs text-gray-700 overflow-x-auto">{{ JSON.stringify(node, null, 2) }}</pre>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'SchemaViewer',
  
  data() {
    return {
      stats: null,
      exportReadiness: [],
      selectedNodeType: null,
      nodes: [],
      loading: true
    };
  },
  
  computed: {
    schemaStats() {
      return this.stats?.schema_stats || {};
    },
    nodeTypes() {
      return Object.keys(this.schemaStats.nodes_by_type || {});
    }
  },
  
  mounted() {
    this.fetchData();
  },
  
  methods: {
    async fetchData() {
      try {
        const [statsRes, readinessRes] = await Promise.all([
          this.$axios.get('/api/domain-graph/stats'),
          this.$axios.get('/api/domain-graph/export-readiness').catch(() => ({ data: [] }))
        ]);
        
        this.stats = statsRes.data;
        const readinessData = readinessRes.data;
        this.exportReadiness = Array.isArray(readinessData) ? readinessData : [];
      } catch (error) {
        console.error('Error fetching data:', error);
        this.exportReadiness = [];
      } finally {
        this.loading = false;
      }
    },
    
    async fetchNodesOfType(nodeType) {
      try {
        const response = await this.$axios.get(`/api/domain-graph/schema/nodes/${nodeType}`);
        this.nodes = response.data.nodes;
        this.selectedNodeType = nodeType;
      } catch (error) {
        console.error('Error fetching nodes:', error);
      }
    }
  }
};
</script>

<style scoped>
.space-y-6 > * + * {
  margin-top: 1.5rem;
}

.space-y-3 > * + * {
  margin-top: 0.75rem;
}

.space-y-2 > * + * {
  margin-top: 0.5rem;
}

.space-y-1 > * + * {
  margin-top: 0.25rem;
}

.space-x-2 > * + * {
  margin-left: 0.5rem;
}

.gap-3 {
  gap: 0.75rem;
}
</style>
